import React, { useMemo } from 'react';
import { Form, Col, Row, Card, Alert } from 'react-bootstrap';

import Button from '../../../components/Button';

/** JSON armazenado no backend: array de strings (nomes). */
export function parseNomesEnvolvidosJson(json: string | null | undefined): string[] {
	if (!json || typeof json !== 'string') {
		return [];
	}
	try {
		const parsed = JSON.parse(json);
		if (Array.isArray(parsed)) {
			return parsed
				.map((item) => (typeof item === 'string' ? item.trim() : String(item ?? '').trim()))
				.filter(Boolean);
		}
	} catch {
		return [];
	}
	return [];
}

export function stringifyNomesEnvolvidos(nomes: string[]): string | null {
	const filtrados = nomes.map((n) => n.trim()).filter(Boolean);
	if (filtrados.length === 0) {
		return null;
	}
	return JSON.stringify(filtrados);
}

export interface AssinaturaComNomesAdicionaisProps {
	/** Rótulo da seção (ex.: "assinatura") */
	label?: string;
	assinaturaUsuario: string | null;
	assinaturaBase64: string | null;
	onAssinaturaBase64Change: (value: string | null) => void;
	nomesAdicionais: string[];
	onNomesAdicionaisChange: (nomes: string[]) => void;
	mostrarAssinatura: boolean;
	onMostrarAssinaturaChange: (show: boolean) => void;
}

export default function AssinaturaComNomesAdicionais({
	label = 'assinatura',
	assinaturaUsuario,
	assinaturaBase64,
	onAssinaturaBase64Change,
	nomesAdicionais,
	onNomesAdicionaisChange,
	mostrarAssinatura,
	onMostrarAssinaturaChange,
}: AssinaturaComNomesAdicionaisProps) {
	const handleAssinar = () => {
		if (assinaturaUsuario) {
			onAssinaturaBase64Change(assinaturaUsuario);
			onMostrarAssinaturaChange(false);
		}
	};

	const handleRemoverAssinatura = () => {
		onAssinaturaBase64Change(null);
		onMostrarAssinaturaChange(false);
	};

	const adicionarNome = () => {
		onNomesAdicionaisChange([...nomesAdicionais, '']);
	};

	const atualizarNome = (index: number, valor: string) => {
		const next = [...nomesAdicionais];
		next[index] = valor;
		onNomesAdicionaisChange(next);
	};

	const removerNome = (index: number) => {
		onNomesAdicionaisChange(nomesAdicionais.filter((_, i) => i !== index));
	};

	const linhasNomes = useMemo(() => {
		if (nomesAdicionais.length === 0) {
			return null;
		}
		return nomesAdicionais.map((nome, index) => (
			<Form.Row className="row-custom align-items-end" key={`nome-envolvido-${index}`}>
				<Form.Group as={Col} md={10}>
					{index === 0 ? (
						<Form.Label className="text-muted small mb-1">Demais envolvidos (nome)</Form.Label>
					) : null}
					<Form.Control
						value={nome}
						onChange={(e) => atualizarNome(index, e.target.value)}
						placeholder="Nome completo"
					/>
				</Form.Group>
				<Form.Group as={Col} md={2}>
					<Button
						type="button"
						title="Remover"
						contained={false}
						onClick={() => removerNome(index)}
					/>
				</Form.Group>
			</Form.Row>
		));
	}, [nomesAdicionais]);

	return (
		<Form.Row className="row-custom">
			<Form.Group as={Col} md={12}>
				<Form.Label>
					<strong>{label}</strong>
					{assinaturaBase64 && (
						<i
							className="fas fa-check-circle"
							style={{
								color: '#28a745',
								marginLeft: '10px',
								fontSize: '18px',
							}}
						/>
					)}
				</Form.Label>

				{!mostrarAssinatura ? (
					<div
						onClick={() => onMostrarAssinaturaChange(true)}
						style={{
							border: assinaturaBase64 ? '2px solid #28a745' : '2px dashed #6c757d',
							borderRadius: '8px',
							padding: '30px 20px',
							textAlign: 'center',
							cursor: 'pointer',
							backgroundColor: assinaturaBase64 ? '#f0f9f4' : '#f8f9fa',
							transition: 'all 0.3s',
							position: 'relative',
							overflow: 'hidden',
						}}
						onMouseEnter={(e) => {
							e.currentTarget.style.backgroundColor = assinaturaBase64 ? '#e6f4ea' : '#e9ecef';
						}}
						onMouseLeave={(e) => {
							e.currentTarget.style.backgroundColor = assinaturaBase64 ? '#f0f9f4' : '#f8f9fa';
						}}
					>
						{assinaturaBase64 ? (
							<>
								<div
									style={{
										filter: 'blur(3px)',
										opacity: 0.6,
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										minHeight: '60px',
									}}
								>
									<img
										src={assinaturaBase64}
										alt="Assinatura"
										style={{
											maxWidth: '200px',
											maxHeight: '60px',
											height: 'auto',
										}}
									/>
								</div>
								<div
									style={{
										position: 'absolute',
										top: '50%',
										left: '50%',
										transform: 'translate(-50%, -50%)',
										color: '#28a745',
										fontWeight: 600,
										fontSize: '14px',
										backgroundColor: 'rgba(255, 255, 255, 0.95)',
										padding: '5px 15px',
										borderRadius: '20px',
										border: '1px solid #28a745',
									}}
								>
									✓ Assinado - Clique para visualizar ou alterar
								</div>
							</>
						) : (
							<>
								<i
									className="fas fa-signature"
									style={{
										fontSize: '24px',
										color: '#6c757d',
										marginBottom: '10px',
										display: 'block',
									}}
								/>
								<div style={{ color: '#6c757d', fontWeight: 500 }}>Clique aqui para assinar</div>
								<small className="text-muted d-block mt-2">Sua assinatura cadastrada será aplicada</small>
							</>
						)}
					</div>
				) : (
					<Card style={{ marginTop: '10px' }}>
						<Card.Body>
							{assinaturaUsuario ? (
								<>
									<div style={{ textAlign: 'center', marginBottom: '15px' }}>
										<strong>Sua Assinatura:</strong>
									</div>
									<div
										style={{
											display: 'flex',
											justifyContent: 'center',
											padding: '20px',
											backgroundColor: '#f8f9fa',
											borderRadius: '4px',
											marginBottom: '15px',
										}}
									>
										<img
											src={assinaturaUsuario}
											alt="Sua assinatura"
											style={{
												maxWidth: '300px',
												maxHeight: '100px',
												height: 'auto',
												border: '1px solid #dee2e6',
												borderRadius: '4px',
												padding: '10px',
												backgroundColor: '#fff',
											}}
										/>
									</div>
									<div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
										<Button
											title="Cancelar"
											contained={false}
											type="button"
											onClick={() => onMostrarAssinaturaChange(false)}
										/>
										{assinaturaBase64 && (
											<Button
												title="Remover Assinatura"
												contained={false}
												type="button"
												onClick={handleRemoverAssinatura}
											/>
										)}
										<Button
											title={assinaturaBase64 ? 'Alterar Assinatura' : 'Confirmar Assinatura'}
											contained
											type="button"
											onClick={handleAssinar}
										/>
									</div>
								</>
							) : (
								<Alert variant="warning">
									Você ainda não possui uma assinatura cadastrada.
									<br />
									Acesse <strong>Caderno LPMA → Registros → Assinaturas</strong> para cadastrar.
								</Alert>
							)}
						</Card.Body>
					</Card>
				)}

				<div className="mt-3">
					<Button type="button" title="+ Adicionar nome" contained={false} onClick={adicionarNome} />
				</div>
				{linhasNomes}
			</Form.Group>
		</Form.Row>
	);
}
